import { GUI } from 'dat.gui';
import Stats from 'stats.js';
import { DebugPanelReplicant } from './extendReplicantConfigForDebugPanel';
import { ABTestsConfig, ClientReplicant } from '@play-co/replicant';
import VConsole from 'vconsole';

let gui: GUI;

export interface NumberSchema {
    min?: number;
    max?: number;
    step?: number;
    get(): number | PromiseLike<number>;
    set(value: number): void;
}

export interface StringSchema {
    choices?: string[];
    get(): string | PromiseLike<string>;
    set(value: string): void;
}

export interface BooleanSchema {
    get(): boolean | PromiseLike<boolean>;
    set(value: boolean): void;
}

export type ButtonSchema = () => void | PromiseLike<void>;

export type ValueSchema = NumberSchema | StringSchema | BooleanSchema | ButtonSchema;

export type UISchema = Record<string, Record<string, ValueSchema>>;

export function setExpanded(expanded: boolean) {
    if (gui) {
        gui.closed = !expanded;
    }
}

export function setVisible(visible: boolean) {
    if (gui) {
        gui.domElement.style.visibility = visible ? '' : 'hidden';
    }
}

export interface ConfigureDebugPanelOptions<T extends DebugPanelReplicant> {
    /** Your game's Replicant client. */
    replicant: ClientReplicant<T>;

    ui?: UISchema | undefined;
}

async function configureDebugPanelAsync<T extends DebugPanelReplicant>({
    replicant,
    ui,
}: ConfigureDebugPanelOptions<T>) {
    GUI.TEXT_OPEN = '⚙️ Debug';
    GUI.TEXT_CLOSED = 'Hide';

    function createClockOffsetButton(deltaTime: number): ButtonSchema {
        return async () => {
            const clockOffset = await replicant.invoke.debugPanel_getClockOffset(undefined);
            await replicant.invoke.debugPanel_setClockOffset({ clockOffset: clockOffset + deltaTime });
        };
    }

    const abTests: ABTestsConfig = replicant.config.ruleset?.abTests ?? {};

    const schema: UISchema = {
        '📈 A/B Tests': Object.fromEntries(
            Object.keys(abTests)
                .sort()
                .reverse()
                .map((testId) => {
                    const availableBuckets = abTests[testId]?.buckets?.map((bucket) => bucket.id) ?? [];
                    return [
                        testId,
                        {
                            choices: availableBuckets,
                            get() {
                                return replicant.abTests.getBucketID(testId as never) ?? '';
                            },
                            set(bucket: string) {
                                void replicant.invoke.debugPanel_assign({ testId, bucket });
                            },
                        },
                    ];
                }),
        ),
        '🕗 Time Machine': {
            'Offset (mins)': {
                async get() {
                    const clockOffset = await replicant.invoke.debugPanel_getClockOffset(undefined);
                    return clockOffset / (60 * 1000);
                },
                set(clockOffset: number) {
                    void replicant.invoke.debugPanel_setClockOffset({ clockOffset });
                },
            },
            '-1 Minute': createClockOffsetButton(-60 * 1000),
            '+1 Minute': createClockOffsetButton(60 * 1000),
            '-1 Hour': createClockOffsetButton(-60 * 60 * 1000),
            '+1 Hour': createClockOffsetButton(60 * 60 * 1000),
            '-1 Day': createClockOffsetButton(-24 * 60 * 60 * 1000),
            '+1 Day': createClockOffsetButton(24 * 60 * 60 * 1000),
            '-30 Days': createClockOffsetButton(-30 * 24 * 60 * 60 * 1000),
            '+30 Days': createClockOffsetButton(30 * 24 * 60 * 60 * 1000),
            Reset: async () => {
                await replicant.invoke.debugPanel_setClockOffset({ clockOffset: 0 });
            },
        },
        ...ui,
    };

    const state: Record<string, Record<string, number | boolean | string | (() => void)>> = {};
    async function updateState() {
        for (const folderName in schema) {
            const folderSchema = schema[folderName]!;

            let folderState = state[folderName];
            if (!folderState) {
                folderState = {};
                state[folderName] = folderState;
            }

            for (const controllerName in folderSchema) {
                const controllerSchema = folderSchema[controllerName]!;
                if ('get' in controllerSchema) {
                    folderState[controllerName] = await controllerSchema.get();
                } else if (typeof controllerSchema === 'function') {
                    folderState[controllerName] = async () => {
                        await controllerSchema();
                        await updateDisplay();
                    };
                }
            }
        }
    }

    async function updateDisplay() {
        await updateState();
        gui.updateDisplay();
    }

    await updateState();

    const stats = new Stats();
    stats.showPanel(0);

    requestAnimationFrame(function update() {
        stats.update();
        requestAnimationFrame(update);
    });

    const vConsole = new VConsole({ theme: 'dark' });

    function onOpen() {
        gui.width = 245;
        document.body.appendChild(stats.dom);
        vConsole.showSwitch();
    }

    function onClose() {
        gui.width = 100;
        stats.dom.remove();
        vConsole.hideSwitch();
    }

    let _closed = false;
    gui = new GUI({
        // Prevent hiding by pressing the H key
        hideable: false,

        set closed(closed) {
            if (closed === _closed) {
                return;
            }
            _closed = closed;
            if (closed) {
                onClose();
            } else {
                onOpen();
                void updateDisplay();
            }
        },
        get closed() {
            return _closed;
        },
    });

    // Kinda hack to make sure the panel is always on top
    gui.domElement.parentElement!.style.zIndex = '9999';

    // Remember UI state
    gui.useLocalStorage = true;
    if ((gui.load as any).closed === false) {
        onOpen();
    } else {
        gui.close();
    }

    // Build the UI
    for (const folderName in schema) {
        const folderSchema = schema[folderName]!;
        const folderState = state[folderName]!;

        const folder = gui.addFolder(folderName);

        for (const controllerName in folderSchema) {
            const controllerSchema = folderSchema[controllerName]!;

            let controller;
            if ('choices' in controllerSchema) {
                controller = folder.add(folderState, controllerName, controllerSchema.choices!);
            } else if ('min' in controllerSchema || 'max' in controllerSchema || 'step' in controllerSchema) {
                controller = folder.add(
                    folderState,
                    controllerName,
                    controllerSchema.min,
                    controllerSchema.max,
                    controllerSchema.step,
                );
            } else {
                controller = folder.add(folderState, controllerName);
            }
            if ('set' in controllerSchema) {
                controller.onChange(controllerSchema.set);
            }
        }
    }
}

export function configureDebugPanel<T extends DebugPanelReplicant>(opts: ConfigureDebugPanelOptions<T>) {
    void configureDebugPanelAsync(opts);
}
